import type { QueryClient } from '@tanstack/react-query'

import { apiClient, getPage } from '@/api/client'
import { marketEndpoints } from '@/api/endpointCache'
import type { paths } from '@/api/schema'
import { filterParams, queryParams } from '@/features/screener/lib/filters'
import { money, type Money } from '@/lib/money'
import { blankToNull, lenientList } from '@/schemas/common'
import {
  metricListSchema,
  screenerPageSchema,
  screenerSectorSchema,
  type Metric,
  type ScreenFilter,
  type ScreenerPage,
  type ScreenerRow,
  type ScreenerSector,
  type ScreenQuery,
  type ScreenSort,
} from '@/schemas/screener'

type ScreenParams = NonNullable<paths['/api/v5/screener/']['get']['parameters']['query']>

/** mm-be's ceiling on `page_size`: the every-page read asks for this many at a time. */
export const MAX_PAGE_SIZE = 200

/** The row's own fields; every other key it carries is a metric figure. */
const IDENTITY_FIELDS: ReadonlySet<string> = new Set([
  'symbol',
  'name',
  'sector_symbol',
  'sector_name',
  'date',
])

const DECIMAL = /^-?\d+(\.\d+)?$/

/** A figure as served: a decimal string, or an int (`volume`). Anything else is "not computed". */
const toFigure = (value: unknown): Money | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? money(value) : null
  return typeof value === 'string' && DECIMAL.test(value) ? money(value) : null
}

const text = (value: unknown): string | null =>
  typeof value === 'string' ? blankToNull(value) : null

/**
 * Read as a record, not as the spec's `ScreenerStock`: the columns are whatever `/metrics/` lists,
 * so a figure mm-be adds reaches the table without a regenerated type.
 */
export const mapScreenerRow = (wire: Record<string, unknown>) => ({
  symbol: wire['symbol'],
  name: wire['name'],
  sectorSymbol: text(wire['sector_symbol']),
  sectorName: text(wire['sector_name']),
  values: Object.fromEntries(
    Object.entries(wire)
      .filter(([key]) => !IDENTITY_FIELDS.has(key))
      .map(([key, value]) => [key, toFigure(value)]),
  ),
})

/**
 * The keys are checked against `/metrics/` before they get here (`knownRanges`), which is why a
 * plain record stands in for the spec's per-key query type: mm-be 400s an unknown one.
 */
const asParams = (params: Record<string, string | number>) => params as ScreenParams

const screenPage = async (params: Record<string, string | number>): Promise<ScreenerPage> => {
  const page = await getPage<Record<string, unknown>>(() =>
    apiClient.GET('/api/v5/screener/', { params: { query: asParams(params) } }),
  )
  return screenerPageSchema.parse({
    rows: page.rows.map(mapScreenerRow),
    page: page.meta?.page ?? 1,
    pageSize: page.meta?.page_size ?? page.rows.length,
    total: page.meta?.total ?? page.rows.length,
  })
}

/** Both reads need a session; a signed-out member never reaches the route. */
export const screenerService = {
  /** Every metric a screen can show, filter and sort on, in display order. */
  metrics: async (): Promise<Metric[]> => {
    // Typed `unknown` on purpose: the spec's type for this path is another serializer's (B1).
    const page = await getPage<unknown>(() => apiClient.GET('/api/v5/screener/metrics/'))
    return metricListSchema.parse(page.rows)
  },

  /** One page of the stocks that match. */
  screen: (query: ScreenQuery): Promise<ScreenerPage> => screenPage(queryParams(query)),

  /** How many stocks match: a one-row page, for its `total`. */
  count: async (filter: ScreenFilter): Promise<number> =>
    (await screenPage({ ...filterParams(filter), page: 1, page_size: 1 })).total,

  /**
   * Every matching stock, in the screen's order: the heatmap draws them all and the CSV writes
   * them all. The listed market is about 300 names, so two pages of 200; the rest of the pages go
   * together once the first has said how many there are.
   */
  every: async (filter: ScreenFilter, sort: ScreenSort | null): Promise<ScreenerRow[]> => {
    const ask = (page: number) =>
      screenPage(queryParams({ ...filter, sort, page, pageSize: MAX_PAGE_SIZE }))
    const first = await ask(1)
    const pages = Math.ceil(first.total / MAX_PAGE_SIZE)
    const rest = await Promise.all(
      Array.from({ length: Math.max(0, pages - 1) }, (_, index) => ask(index + 2)),
    )
    return [first, ...rest].flatMap((page) => page.rows)
  },

  /** The sectors a screen can be narrowed to, A–Z: `/market/sectors/`'s keys are what `sector=` takes. */
  sectors: async (client?: QueryClient): Promise<ScreenerSector[]> => {
    const page = await marketEndpoints(client).sectors('az')
    return lenientList(screenerSectorSchema, 'sector').parse(
      page.rows.map((row) => ({ key: row.key, name: row.name })),
    )
  },
}
